import { useCallback, useEffect } from 'react';
import { connect } from 'react-redux';
import cn from 'classnames';
import { bindActionCreators } from 'redux';

import fetchSalon from 'actions/fetchSalon.action';
import ICON_CLOUD from 'assets/images/online-booking/cloud-arrow-up.svg';
import IMG_NOT_FOUND from 'assets/images/online-booking/no-image-available.png';
import SalonModal from 'components/dialog/SalonModal.component';
import Button from 'components/form/Button.component';
import Space from 'components/generic/Space.component';
import withSalonId from 'components/hoc/withSalonId.hoc';
import useAef from 'hooks/useAef.hook';
import useAfn from 'hooks/useAfn.hook';
import useStateWhileMounted from 'hooks/useStateWhileMounted.hook';
import useTip from 'hooks/useTip.hook';
import { useTranslation } from 'providers/GILT.provider';
import { withTheme } from 'providers/Theme.provider';
import getArray from 'utils/accessors/getArray.util';
import fromFileToPromiseString from 'utils/converters/dom/fromFileToPromiseString.util';
import fromAnyToBool from 'utils/converters/fromAnyToBool.util';
import logger from 'utils/logger.util';
import asObject from 'utils/objects/asObject.util';

import beGetSettingsNcob from 'settings/actions/beGetSettingsNcob';
import bePutSettingsNcob from 'settings/actions/bePutSettingsNcob';

import CN from './FileUpload.module.scss';

const L = logger('FileUpload');
// 1 mb = 1 048 576 b
const FILE_SIZE_LIMIT = 2097152; //  2 097 152 === 2mb

const FileUpload = ({ isDarkMode, fetchSalon, salonId, beGetSettingsNcob, bePutSettingsNcob }) => {
  const { val: hasError, set: setError, nay: clearError } = useTip(false);
  const [ncobSettings, setNcobSettings] = useStateWhileMounted();
  const [selectedImage, setSelectedImage] = useStateWhileMounted(null);

  const { t } = useTranslation();

  const handleChange = useCallback(
    (ev) => {
      const firstFile = getArray('target.files.0', ev);
      const isOverCapacity = asObject(firstFile).size >= FILE_SIZE_LIMIT;
      setError(isOverCapacity);
      setSelectedImage(isOverCapacity ? null : firstFile);
    },
    [setError, setSelectedImage],
  );

  const [{ value: valueSaveImage, error: errorSaveImage, isLoading: isLoadingSaveImage, isLoaded: isLoadedSaveImage }, handleSaveNewImage] = useAfn(async () => {
    if (!selectedImage) {
      L.debug('handleSaveNewImage()', 'No selectedImage:', selectedImage);
      return null;
    }

    const profileImage = await fromFileToPromiseString(selectedImage);
    const result = await bePutSettingsNcob({ data: { profile_image: profileImage } });

    if (!result.ok) L.warn('handleSaveNewImage()', 'result:', result);

    setNcobSettings(result.json);
    setSelectedImage(null); // Hide new image section after save

    return result.json;
  }, [bePutSettingsNcob, selectedImage, setNcobSettings, setSelectedImage]);

  const { value: valueSettingsNcob, error: errorSettingsNcob } = useAef(async () => {
    const { json } = await beGetSettingsNcob({ salonId });
    setNcobSettings(json);
    return json;
  }, [beGetSettingsNcob, salonId, setNcobSettings]);

  useEffect(
    // NOTE: this might be a performance hit, so it should not be used every time, but only if salon data is missing
    () => void L.unignore('useEffect()', 'fetchSalon:', fetchSalon(salonId)),
    [fetchSalon, salonId],
  );

  const currentImageUrl = asObject(ncobSettings).profile_image || IMG_NOT_FOUND;

  const error = errorSettingsNcob || errorSaveImage;
  if (error) L.warn('()', { error, valueSettingsNcob, valueSaveImage, isLoadingSaveImage, isLoadedSaveImage });

  return (
    <div
      data-bem="FileUpload"
      className={cn({
        [CN.component]: true,
        [CN.darkMode]: isDarkMode,
      })}
    >
      <div className={CN.imagePreview}>
        <div className={CN.logoContainer}>
          <div className={CN.label}>{t('composite.fileUpload.currentLogo')}</div>
          <div className={CN.imageContainer}>
            <img src={currentImageUrl} className={CN.currentImage} alt={t('composite.fileUpload.profileLogo')} />
          </div>
        </div>

        {selectedImage && !isLoadedSaveImage && (
          <div className={CN.logoContainer}>
            <div className={CN.label}>{t('composite.fileUpload.newLogo')}</div>

            <div className={CN.imageContainer}>
              <img alt={t('composite.fileUpload.notFound')} className={CN.newImage} src={URL.createObjectURL(selectedImage)} />
              <div className={CN.fileInfo}>
                <strong>{t('composite.fileUpload.name')} </strong>
                {selectedImage.name}
              </div>
            </div>
          </div>
        )}
      </div>
      <div className={CN.uploadButton}>
        <label htmlFor="fileUpload" className={CN.customFileUpload}>
          <img src={ICON_CLOUD} alt={t('composite.fileUpload.clickUploadFile')} className={CN.fileIcon} />
          <strong>{t('composite.fileUpload.clickUpload')}</strong> <br />
          {t('composite.fileUpload.fileRestriction')}
        </label>

        <input id="fileUpload" type="file" onChange={handleChange} />
        {selectedImage && (
          <Button variant="primary" onClick={handleSaveNewImage}>
            {t('composite.fileUpload.saveImage')}
          </Button>
        )}
      </div>

      {fromAnyToBool(hasError) && (
        <SalonModal tag="FileUpload" isOpen={hasError}>
          <SalonModal.Body>
            <span className={CN.modalTitle}>{t('common.error')}</span>
            <Space>
              <span className={CN.modalText}>{t('composite.fileUpload.errorMessage')}</span>
            </Space>
          </SalonModal.Body>
          <SalonModal.Footer>
            <Button variant="slim" onClick={clearError}>
              {t('common.buttons.closeUpp')}
            </Button>
          </SalonModal.Footer>
        </SalonModal>
      )}
    </div>
  );
};

//
const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      fetchSalon,
      beGetSettingsNcob,
      bePutSettingsNcob,
    },
    dispatch,
  );

const FileUploadHoc = connect(null, mapDispatchToProps)(withTheme()(withSalonId(FileUpload)));

export default FileUploadHoc;
